import { networkFetch, resetApiSession } from "./api";
import { setPhotoSession } from "./photo-cache";

const ISSUER = (import.meta.env.VITE_AUTHENTIK_ISSUER ?? "").replace(/\/?$/, "/");
const CLIENT_ID = import.meta.env.VITE_AUTHENTIK_CLIENT_ID ?? "";
const REDIRECT_URI =
  import.meta.env.VITE_AUTHENTIK_REDIRECT_URI ?? `${window.location.origin}/callback`;
const SCOPES = import.meta.env.VITE_AUTHENTIK_SCOPES ?? "openid email wineps:read offline_access";
const AUTHENTIK_PROXY = (import.meta.env.VITE_AUTHENTIK_PROXY ?? "").replace(/\/$/, "");
const SESSION_KEY = "wineps-oauth-session";
const TRANSACTION_KEY = "wineps-oauth-transaction";

interface OAuthSession {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
}

interface OAuthMetadata {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint?: string;
}

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

function browserEndpoint(url: string): string {
  if (!AUTHENTIK_PROXY) return url;
  const endpoint = new URL(url);
  return `${AUTHENTIK_PROXY}${endpoint.pathname}${endpoint.search}`;
}

function encode(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function randomValue(): string {
  return encode(crypto.getRandomValues(new Uint8Array(32)));
}

async function sha256(value: string): Promise<string> {
  return encode(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value))));
}

async function metadata(): Promise<OAuthMetadata> {
  if (!ISSUER || !CLIENT_ID) throw new Error("La configuration Authentik de la PWA est incomplète.");
  const response = await networkFetch(
    browserEndpoint(`${ISSUER}.well-known/openid-configuration`)
  );
  if (!response.ok) throw new Error("Authentik est momentanément indisponible.");
  return response.json() as Promise<OAuthMetadata>;
}

function readSession(): OAuthSession | null {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as OAuthSession;
  } catch {
    sessionStorage.removeItem(SESSION_KEY);
    resetApiSession();
    return null;
  }
}

function saveToken(token: TokenResponse, previousRefreshToken?: string): OAuthSession {
  const session = {
    accessToken: token.access_token,
    refreshToken: token.refresh_token ?? previousRefreshToken,
    expiresAt: Date.now() + Math.max(token.expires_in - 30, 0) * 1000,
  };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  setPhotoSession(session.accessToken);
  return session;
}

async function tokenRequest(parameters: URLSearchParams): Promise<TokenResponse> {
  const config = await metadata();
  const response = await networkFetch(browserEndpoint(config.token_endpoint), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: parameters,
  });
  if (!response.ok) throw new Error("La session Authentik n’a pas pu être créée.");
  return response.json() as Promise<TokenResponse>;
}

export type LoginMode = "wineps" | "google";

export async function login(mode: LoginMode = "wineps"): Promise<void> {
  const config = await metadata();
  const verifier = randomValue();
  const state = randomValue();
  sessionStorage.setItem(TRANSACTION_KEY, JSON.stringify({ verifier, state }));
  const query = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: "code",
    scope: SCOPES,
    state,
    code_challenge: await sha256(verifier),
    code_challenge_method: "S256",
  });
  window.location.assign(`${config.authorization_endpoint}?${query}`);
}

export async function completeLogin(): Promise<void> {
  const query = new URLSearchParams(window.location.search);
  const code = query.get("code");
  const state = query.get("state");
  const raw = sessionStorage.getItem(TRANSACTION_KEY);
  const transaction = raw ? JSON.parse(raw) as { verifier: string; state: string } : null;
  sessionStorage.removeItem(TRANSACTION_KEY);
  if (!code || !state || !transaction || state !== transaction.state) {
    throw new Error("La réponse Authentik ne correspond pas à la connexion initiée.");
  }
  const token = await tokenRequest(new URLSearchParams({
    grant_type: "authorization_code",
    client_id: CLIENT_ID,
    code,
    redirect_uri: REDIRECT_URI,
    code_verifier: transaction.verifier,
  }));
  saveToken(token);
  window.history.replaceState({}, "", "/");
}

export async function accessToken(): Promise<string | null> {
  const session = readSession();
  if (!session) { resetApiSession(); return null; }
  if (session.expiresAt > Date.now()) {
    setPhotoSession(session.accessToken);
    return session.accessToken;
  }
  if (!session.refreshToken) {
    sessionStorage.removeItem(SESSION_KEY);
    resetApiSession();
    return null;
  }
  try {
    const token = await tokenRequest(new URLSearchParams({
      grant_type: "refresh_token",
      client_id: CLIENT_ID,
      refresh_token: session.refreshToken,
    }));
    return saveToken(token, session.refreshToken).accessToken;
  } catch {
    sessionStorage.removeItem(SESSION_KEY);
    resetApiSession();
    return null;
  }
}

export async function logout(): Promise<void> {
  sessionStorage.removeItem(SESSION_KEY);
  resetApiSession();
  const config = await metadata().catch(() => null);
  if (config?.end_session_endpoint) {
    const query = new URLSearchParams({
      client_id: CLIENT_ID,
      post_logout_redirect_uri: window.location.origin,
    });
    window.location.assign(`${config.end_session_endpoint}?${query}`);
  } else {
    window.location.assign("/");
  }
}

export function isCallback(): boolean {
  return window.location.pathname === "/callback" && new URLSearchParams(window.location.search).has("code");
}
